import React, { useState } from 'react';
import MemberProfileDialog from './MemberProfileDialog';
import styles from './MemberCard.module.css';
import { isChild, MemberType } from '@/types/member';
import '../../profile/profile.css';
import MBadge from '@/components/ui/atoms/MBadge';
import { formatMonthYear } from '@/utils/date-utils';

export interface MemberCardProps {
  avatar?: React.ReactNode;
  name: string;
  isOwner?: boolean;
  relationship?: string; // New: relationship text for non-owner
  registered?: boolean;
  invited?: boolean;
  member_type?: string;
  dob_year?: number;
  dob_month?: number;
  dob_day?: number;
  onClick?: () => void;
  className?: string;
  menu?: React.ReactNode;
  accessLabel?: string; // Show access label in place of registered label
  showRegistrationLabels?: boolean; // Only show registered/invited in index.tsx
  accessStatus?: string; // Show special status like Consent Pending
  accessStatusClass?: string; // Custom class for accessStatus label
  hasAccess?: boolean; // Permission prop for parent logic
  loggedInMember?: boolean;
  member?: any;
  hideViewProfileLink?: boolean;
  showCTA?: { text: string; onClick?: () => void };
}

const MemberCard: React.FC<MemberCardProps & { showStatusLabels?: boolean }> = ({
  avatar,
  name,
  isOwner,
  relationship,
  registered,
  invited,
  member_type,
  dob_year,
  dob_month,
  dob_day,
  onClick,
  className = '',
  menu,
  accessLabel,
  showRegistrationLabels,
  accessStatus,
  accessStatusClass,
  loggedInMember,
  member,
  hideViewProfileLink,
  showCTA,
  showStatusLabels = false,
}) => {
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  return (
    <>
      <div className={`${styles.memberCard} ${className}`} onClick={onClick}>
      <div className={styles.row}>
        {avatar && <div className={styles.avatar}>{React.cloneElement(avatar as React.ReactElement, { size: 'md', showBadge: false })}</div>}
        <div className={styles.info}>
          <div className={styles.name} style={{display: 'flex', alignItems: 'center'}}>
            {name}
            {/* Show 'You' badge if this is the logged-in member */}
            {loggedInMember && (
              <MBadge variant="purple-inverse" className="ml-2">You</MBadge>
            )}
          </div>
          
          {/* {isOwner ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <MBadge>Owner</MBadge>
              {accessLabel && (
                <span style={{
                  background: 'var(--colour-background-brand-primary-50, #EDE8F5)',
                  color: '#45168C',
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 11,
                  padding: '2px 8px',
                }}>{accessLabel}</span>
              )}
            </div>
          ) : (
            // For Pet and Child: show only relationship label
            (member_type === MemberType.PET || isChild({ dob_year, dob_month, dob_day })) ? (
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                <span className={`${styles.statusLabel} ${styles.statusLabelUnregistered}`}>Unregistered</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                {showRegistrationLabels && (typeof registered !== 'undefined' || typeof invited !== 'undefined') && (
                  invited ? (
                    <span className={`${styles.statusLabel} ${styles.statusLabelInvited}`}>Invited</span>
                  ) : registered ? (
                    <span className={`${styles.statusLabel} ${styles.statusLabelRegistered}`}>Registered</span>
                  ) : (
                    <span className={`${styles.statusLabel} ${styles.statusLabelUnregistered}`}>Unregistered</span>
                  )
                )}
                {accessStatus && (
                  <span className={`${styles.statusLabel} ${accessStatusClass ? styles[accessStatusClass] : styles.statusLabelUnregistered}`}>{accessStatus}</span>
                )}
                {accessLabel && (
                  <span style={{
                    background: 'var(--colour-background-brand-primary-50, #EDE8F5)',
                    color: '#45168C',
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: 11,
                    padding: '2px 8px',
                  }}>{accessLabel}</span>
                )}
              </div>
            )
          )} */}
          {showStatusLabels && (
            isOwner ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MBadge>Owner</MBadge>
                {accessLabel && (
                  <span style={{
                    background: 'var(--colour-background-surface-lvl-0-1, #EDEDED)',
                      color: '#474747',
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: 11,
                    padding: '2px 8px',
                    whiteSpace: 'nowrap'
                  }}>{accessLabel}</span>
                )}
              </div>
            ) : (
              // For Pet and Child: show only relationship label
              (member_type === MemberType.PET || isChild({ dob_year, dob_month, dob_day })) ? (
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <span className={`${styles.statusLabel} ${styles.statusLabelUnregistered}`}>Unregistered</span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  {showRegistrationLabels && (typeof registered !== 'undefined' || typeof invited !== 'undefined') && (
                    invited ? (
                      <span className={`${styles.statusLabel} ${styles.statusLabelInvited}`}>Invited</span>
                    ) : registered ? (
                      <span className={`${styles.statusLabel} ${styles.statusLabelRegistered}`}>Registered</span>
                    ) : (
                      <span className={`${styles.statusLabel} ${styles.statusLabelUnregistered}`}>Unregistered</span>
                    )
                  )}
                  {accessStatus && (
                    <span className={`${styles.statusLabel} ${accessStatusClass ? styles[accessStatusClass] : styles.statusLabelUnregistered}`}>{accessStatus}</span>
                  )}
                  {accessLabel && (
                    <span style={{
                      background: 'var(--colour-background-surface-lvl-0-1, #EDEDED)',
                      color: '#474747',
                      borderRadius: 8,
                      fontWeight: 600,
                      fontSize: 11,
                      padding: '2px 8px',
                    }}>{accessLabel}</span>
                  )}
                </div>
              )
            )
          )}
          {!showStatusLabels && !hideViewProfileLink && (
            <span className="body-xs-semibold cursor-pointer" onClick={e => { e.stopPropagation(); setProfileDialogOpen(true); }}>View profile &gt;</span>
          )}
          {showCTA && (
            <div className="member-card-cta">
              <button
                className="member-card-cta-btn"
                onClick={showCTA.onClick}
                type="button"
              >
                <span className="member-card-cta-text">{showCTA.text}</span>
              </button>
            </div>
          )}
        </div>
        <div className={styles.menu}>
          {menu}
        </div>
      </div>
    </div>
      <MemberProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        member={{
          id: member?.id,
          avatar: (avatar && typeof avatar === 'object' && 'props' in avatar) ? (avatar as any).props.src : undefined,
          name: member?.firstName && member?.lastName ? `${member.firstName} ${member.lastName}` : member?.firstName || '',
          birthDetails: member?.dobMonth && member?.dobYear ? formatMonthYear(member.dobMonth, member.dobYear) : '',
          mobile: member?.mobile || '',
          email: member?.email || '',
          totalDocuments: member?.other_attrs?.docsCount ?? 0,
          lastDocumentDate: member?.other_attrs?.lastUploadedDoc,
          emergencyCardReady: member?.emergencyCardReady,
          isPet: member?.memberType === 'p',
          petType: member?.other_attrs?.petType
        }}
        isOwner={isOwner}
        registrationLabel={registered ? 'Registered' : invited ? 'Invited' : 'Unregistered'}
      />
    </>
  );
};

export default MemberCard;
